import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { parseVariation, parseVariationContent } from '../../../user/content/utils/contentParser';
import { VariationMetadata } from './VariationMetadata';
import { SocialShareButton } from './SocialShareButton';

interface VariationCardProps {
  variation: any;
  variationKey: string;
  platformKey: string;
  variationIndex: number;
  copiedStates: Record<string, boolean>;
  onCopy: (text: string, key: string) => void;
  compact?: boolean;
}

export const VariationCard: React.FC<VariationCardProps> = ({
  variation,
  variationKey,
  platformKey,
  variationIndex,
  copiedStates,
  onCopy,
  compact = false,
}) => {
  // Use the parseVariation utility to extract content and metadata
  let { content: variationContent, metadata } = parseVariation(variation, platformKey, variationKey);
  
  // If variationContent is a JSON string, try to parse it
  if (typeof variationContent === 'string' && variationContent.trim().startsWith('{')) {
    const parsed = parseVariationContent(variationContent, platformKey, variationKey);
    if (parsed) {
      variationContent = parsed.content;
      metadata = parsed.metadata || metadata;
    }
  }
  
  // Ensure we have a string, not an object
  let displayContent = '';
  if (typeof variationContent === 'string') {
    displayContent = variationContent.trim();
  } else if (typeof variationContent === 'object' && variationContent !== null) {
    const contentObj = variationContent as any;
    if ('content' in contentObj && typeof contentObj.content === 'string') {
      displayContent = contentObj.content.trim();
    } else {
      displayContent = '';
    }
  } else {
    displayContent = String(variationContent || '').trim();
  }
  
  const copyKey = `${platformKey}_${variationKey}`;

  if (compact) {
    // Compact version for history accordion
    return (
      <Box 
        sx={{ 
          p: 1.5, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: '8px',
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600, 
              textTransform: 'capitalize',
              color: 'text.secondary',
              fontSize: '0.75rem',
              letterSpacing: '0.5px',
            }}
          >
            Variation {variationIndex + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SocialShareButton 
              platformName={platformKey} 
              content={displayContent} 
              size="small" 
            />
            <Tooltip title="Copy content">
              <IconButton
                size="small"
                onClick={() => onCopy(displayContent, copyKey)}
                color={copiedStates[copyKey] ? 'success' : 'default'}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {copiedStates[copyKey] ? <CheckIcon color="success" /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Content */}
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            backgroundColor: 'grey.50',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'grey.200',
            minHeight: 80,
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.5,
              color: 'text.primary',
              fontSize: '0.875rem',
            }}
          >
            {displayContent || 'No content available'}
          </Typography>
        </Box>
        
        <VariationMetadata metadata={metadata} compact={true} />
      </Box>
    );
  }

  // Full version for generated content
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Variation Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
            Variation {variationIndex + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SocialShareButton 
              platformName={platformKey} 
              content={displayContent} 
              size="small" 
            />
            <Tooltip title="Copy variation">
              <IconButton
                size="small"
                onClick={() => onCopy(displayContent, copyKey)}
                color={copiedStates[copyKey] ? 'success' : 'default'}
              >
                {copiedStates[copyKey] ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            mb: 2,
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            minHeight: 150,
          }}
        >
          <Typography
            variant="body1"
            component="div"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
              color: 'text.primary',
            }}
          >
            {displayContent && typeof displayContent === 'string' ? displayContent : 'No content available'}
          </Typography>
        </Box>

        <VariationMetadata metadata={metadata} compact={false} />
      </CardContent>
    </Card>
  );
};

